/**
 * All app state in one reducer. Screens are a state machine, not routes — the
 * app is full-screen on an iPad and the one address it reads is a battle link
 * (D-145), which is consumed once and then cleaned back to `/`.
 *
 * SPEC Section 7 order: age -> roster -> matchup -> facts -> battle -> reveal
 * -> quiz -> collection. Plus `rumble`, the one interstitial (D-099): it sits
 * between the roster and the matchup every time, and between the facts and the
 * battle only while the story is still being written.
 */

import { useEffect, useReducer } from 'react'
import { pendingLink, isBattleLink } from '../lib/share.js'

const AGE_KEY = 'ab.age.v1'

/**
 * The app opens at age 6 (D-133). Three of Jeff's four readers are 6 or will be,
 * and asking a question before showing a single animal cost every first run a
 * screen. The band is still a real setting — the top bar's press-and-hold opens
 * the same picker, and a chosen band is remembered from then on.
 */
export const DEFAULT_AGE = '6'

/** The band this device remembers, or the default. */
function storedAge() {
  let age = null
  try {
    age = localStorage.getItem(AGE_KEY)
  } catch {
    age = null
  }
  // A stored value that is not one of the three bands (an old key, a typo while
  // testing) is ignored, the same as no value at all: the default, not a prompt.
  return ['2', '4', '6'].includes(age) ? age : DEFAULT_AGE
}

const initial = () => {
  const deviceAge = storedAge()
  // A shared battle link (D-145) plays at the band it was shared from, so the
  // story the sender just generated is the one the shared cache hands back. It
  // is the band in force for that battle only: `linkAge` keeps it out of
  // localStorage, and the device returns to its own band when the battle ends.
  const linkAge = isBattleLink(pendingLink) ? pendingLink.age : null
  return {
    age: linkAge || deviceAge,
    deviceAge,
    linkAge: !!linkAge,
    screen: 'roster',
    a: null,
    b: null,
    story: null,
    // The instant, records-only half of the story (D-071, D-099): title, intro,
    // stat rows, every fact page. Set the moment the kid taps Fight; the real
    // story lands alongside it, never replacing it.
    preview: null,
    numbers: null, // the decision arithmetic behind `story` (D-085)
    heard: null,
    status: 'idle', // the story request: idle | loading | error
    error: null,
    fromCache: false,
    // Which interstitial the rumble screen shows, and when it was entered so it
    // can hold its minimum (D-099): ready | battle.
    rumble: 'ready',
    rumbleAt: 0,
    // Which fact page the kid is on. It lives here, not in Facts, because
    // backing out of the battle's first beat returns to the page they left
    // rather than to page one (D-127).
    factsPage: 0,
    // The call (D-135): the slug the kid tapped on the matchup as the winner, or
    // null. Cleared with the pair. The reveal scores it and the collection
    // keeps the count.
    call: null,
    // Which visit to the animals screen this is (D-163). Home means "start
    // over", and every other road back to the roster arrives from another
    // screen, so the screen is rebuilt and its own state — the search text, the
    // category tab — goes with it. Home tapped while already on the roster is
    // the one road that does not, and it left a kid's half-typed search sitting
    // there. Bumping this re-keys the screen so Home is a fresh start wherever
    // it is tapped from.
    visit: 0,
  }
}

/**
 * A shared battle is over: the pair is gone (Home, "Battle again", "Keep the
 * winner", a new pair), so a band that came from the link gives way to the
 * device's own (D-145). Nothing changes when no link band is in force.
 */
const ownBand = (s) => (s.linkAge ? { age: s.deviceAge, linkAge: false } : {})

function reducer(s, action) {
  switch (action.type) {
    case 'setAge':
      // A deliberate change is the device's setting from now on, whatever band a link brought.
      return { ...s, age: action.age, deviceAge: action.age, linkAge: false, screen: s.screen === 'age' ? 'roster' : s.screen }
    case 'go':
      return { ...s, screen: action.screen }
    case 'factsPage':
      return { ...s, factsPage: action.n }
    case 'call':
      // Tap the called animal again to take the call back; tap the other to switch.
      return { ...s, call: s.call === action.slug ? null : action.slug }
    case 'pick': {
      // Tap a selected card to unselect it; otherwise fill a, then b. A pick
      // also dismisses a "Sorry, I couldn't find…" toast from the microphone.
      const slug = action.slug
      const next = s.status === 'error' ? { ...s, status: 'idle', error: null } : s
      if (next.a === slug) return { ...next, a: next.b, b: null }
      if (next.b === slug) return { ...next, b: null }
      if (!next.a) return { ...next, a: slug }
      if (!next.b) return { ...next, b: slug }
      return { ...next, a: slug, b: null } // both full: start over with this one
    }
    case 'setPair':
      // b may be null — the collection's "fight again" and the reveal's "keep
      // the winner" pre-pick one animal.
      return { ...s, ...ownBand(s), a: action.a, b: action.b ?? null, heard: action.heard || null, story: null, preview: null, numbers: null, status: 'idle', error: null, factsPage: 0, call: null }
    case 'clearPair':
      return { ...s, ...ownBand(s), a: null, b: null, story: null, preview: null, numbers: null, heard: null, status: 'idle', error: null, factsPage: 0, call: null }
    case 'start':
      // Fight!: the interstitial, then the matchup from the preview; the story
      // is in flight behind them.
      return {
        ...s,
        a: action.a,
        b: action.b,
        heard: action.heard || null,
        preview: action.preview,
        story: null,
        numbers: null,
        factsPage: 0,
        call: null,
        status: 'loading',
        error: null,
        fromCache: false,
        screen: 'rumble',
        rumble: 'ready',
        rumbleAt: action.now,
      }
    case 'story':
      return { ...s, status: 'idle', story: action.story, numbers: action.numbers ?? null, heard: action.heard ?? s.heard, fromCache: !!action.fromCache }
    case 'error':
      return { ...s, status: 'error', error: action.error }
    case 'toBattle':
      // The battle door: straight in if the story is here, otherwise the
      // interstitial holds it until it is.
      return s.story ? { ...s, screen: 'battle' } : { ...s, screen: 'rumble', rumble: 'battle', rumbleAt: action.now }
    case 'retry':
      return { ...s, status: 'loading', error: null, rumbleAt: action.now }
    case 'reset':
      return { ...s, ...ownBand(s), a: null, b: null, story: null, preview: null, numbers: null, heard: null, status: 'idle', error: null, factsPage: 0, call: null, screen: 'roster', visit: s.visit + 1 }
    default:
      return s
  }
}

export function useGame() {
  const [state, dispatch] = useReducer(reducer, undefined, initial)
  useEffect(() => {
    // A band a shared link brought is never written: a link must not rewrite a
    // device setting (D-145). The device's own band is what gets remembered.
    if (state.linkAge) return
    try {
      if (state.age) localStorage.setItem(AGE_KEY, state.age)
    } catch {
      // ignore
    }
  }, [state.age, state.linkAge])
  return [state, dispatch]
}
